import { useCallback, useEffect, useRef, useState } from 'react';

// Wraps an async handler (submit, save, delete, upload...) so that
//   - `pending` is true while it runs (bind it to a button's `loading`)
//   - a second call while the first is still running is ignored, which is what
//     stops double-clicks and Enter-key repeats from posting twice.
// The guard is a ref, so it blocks a second call made in the same tick, before
// React has re-rendered the disabled button.
//
//   const [save, saving] = useAsyncAction(async values => { await api.save(values); });
//   <ActionButton variant='primary' loading={saving} onClick={save}>Save</ActionButton>
//
// Errors are rethrown to the caller after `pending` is cleared.
const useAsyncAction = action => {
  const [pending, setPending] = useState(false);
  const runningRef = useRef(false);
  const mountedRef = useRef(true);
  const actionRef = useRef(action);
  actionRef.current = action;

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
    };
  }, []);

  const run = useCallback(async (...args) => {
    if (runningRef.current) return undefined;
    runningRef.current = true;
    setPending(true);
    try {
      return await actionRef.current(...args);
    } finally {
      runningRef.current = false;
      if (mountedRef.current) setPending(false);
    }
  }, []);

  return [run, pending];
};

export default useAsyncAction;
